import { useState } from 'react';
import { DASHBOARD_URL, DASHBOARD_UPGRADE_URL } from './config';
import { TopNav } from './TopNav';

interface Props {
  apiBase: string;
  code: string;
}

interface Result {
  ok: boolean;
  channel?: { kind: string; address: string };
  error?: string;
}

// Click-to-verify rather than auto-fire on mount: link scanners
// (Apple Mail preview, Gmail / Outlook SafeLinks, corporate URL
// rewriters) GET this page in the background and used to consume
// the code before the human ever clicked. Now consumption only
// happens on a real button press.
export function Verify({ apiBase, code }: Props) {
  const [state, setState] = useState<'idle' | 'pending' | 'done'>('idle');
  const [result, setResult] = useState<Result | null>(null);

  const verify = async () => {
    setState('pending');
    try {
      const r = await fetch(`${apiBase}/v1/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      setResult(await r.json());
    } catch (e) {
      setResult({ ok: false, error: e instanceof Error ? e.message : String(e) });
    }
    setState('done');
  };

  const page = (children: React.ReactNode) => (
    <div className="container wide">
      <TopNav />
      <section className="verify-card narrow">{children}</section>
    </div>
  );

  if (state === 'idle') {
    return page(
      <>
        <h1>Verify your channel</h1>
        <p>
          Your AI asked to message you here. Click below to confirm it's
          really you.
        </p>
        <p>
          <button type="button" className="cta primary" onClick={verify}>
            Verify now
          </button>
        </p>
      </>,
    );
  }
  if (state === 'pending' || !result) {
    return page(<h1>Verifying…</h1>);
  }
  if (!result.ok) {
    return page(
      <>
        <h1>❌ This link didn't work</h1>
        <p>
          {result.error === 'invalid_or_expired' || !result.error
            ? 'The verification link is invalid or has expired (links last 15 minutes).'
            : result.error}
        </p>
        <p>
          Ask your AI to send you another message — it will issue a fresh
          verification link automatically.
        </p>
      </>,
    );
  }
  return page(
    <>
      <h1>✅ Verified</h1>
      {result.channel && (
        <p>
          Your <strong>{result.channel.kind}</strong> address{' '}
          <code>{result.channel.address}</code> is now linked. Your AI can
          message you here — and you can reply.
        </p>
      )}
      <h2>What's next</h2>
      <ul className="verify-next">
        <li>
          <strong>Reply to the message</strong> your AI just sent — your
          reply lands straight in its inbox.
        </li>
        <li>
          <a href={DASHBOARD_URL}>Open your dashboard</a> to see messages,
          manage channels, and grab your install snippet. Sign in with this
          same channel.
        </li>
        <li>
          Need more than 100 messages a month?{' '}
          <a href={DASHBOARD_UPGRADE_URL}>Go unlimited</a> for $1.65/mo.
        </li>
      </ul>
    </>,
  );
}
